import React, { useEffect } from 'react';
import { Drawer, Form, Input, Button, Space, notification, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sensor } from '../../../../redux/reducers/clients/clients.types';
import { fetchSensorsRequest, clearSensorStatus, updateSensorRequest } from '../../../../redux/reducers/clients/clientsSlice';
import { RootState } from '../../../../redux/store';
import { colors } from '../../../../utils/colors/colors';

const { Option } = Select;

interface UpdateSensorDrawerProps {
    open: boolean;
    onClose: () => void;
    sensorId?: string;
    clientId: string;
}

const UpdateSensorDrawer: React.FC<UpdateSensorDrawerProps> = ({ open, onClose, sensorId, clientId }) => {
    const { t } = useTranslation(['clients', 'common']);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const { data: sensors, loading, success, error } = useSelector((state: RootState) => state.clients.sensors);
    const { data: gateways } = useSelector((state: RootState) => state.clients.gateways);

    const selectedSensor = (sensors || []).find((s: Sensor) => s && s.id === sensorId);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (sensorId) {
                dispatch(updateSensorRequest({ sensorId, data: values }));
            }
        } catch (err) {
            console.error('Validation failed:', err);
        }
    };

    const handleClose = () => {
        form.resetFields();
        dispatch(clearSensorStatus());
        onClose();
    };

    useEffect(() => {
        if (open && selectedSensor) {
            form.setFieldsValue(selectedSensor);
        }
    }, [open, selectedSensor, form]);

    useEffect(() => {
        if (success && open) {
            notification.success({ message: t('clients:sensor_update_success') });
            dispatch(fetchSensorsRequest(clientId));
            handleClose();
        }
        if (error) {
            notification.error({ message: t(error) });
            dispatch(clearSensorStatus());
        }
    }, [success, error, open]);


    return (
        <Drawer
            title={<span style={{ color: colors.blueitNavyBlue, fontWeight: 'bold' }}>{t("clients:edit_sensor")}</span>}
            placement="right"
            onClose={handleClose}
            open={open}
            destroyOnClose
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={handleClose}>{t("common:cancel")}</Button>
                    <Button type="primary" onClick={handleSave} loading={loading}>
                        {t("common:save")}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item name="name" label={t('clients:name')} rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="serialno" label={t('clients:serial_number')} rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="gatewayId" label={t('clients:linked_gateway')} rules={[{ required: true }]}>
                    <Select>
                        {(gateways || []).map(g => <Option key={g.id} value={g.id}>{`${g.gateway} (${g.gsm})`}</Option>)}
                    </Select>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default UpdateSensorDrawer;